import React from "react";
import { Mail, MapPin, ArrowUpRight } from "lucide-react";
import Logo from "@/components/landing/Logo";

export default function Footer() {
  return (
    <footer
      data-testid="footer-section"
      className="relative bg-carvny-ink text-white"
    >
      {/* Top accent rule */}
      <div className="h-px bg-gradient-to-r from-transparent via-carvny-red to-transparent" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-20">
        {/* Massive wordmark */}
        <div className="grid grid-cols-12 gap-y-10 lg:gap-x-12">
          <div className="col-span-12 lg:col-span-7">
            <div data-testid="footer-logo" className="inline-flex">
              <Logo size="lg" invert showTagline={false} testid="footer-logo-mark" />
            </div>
            <p className="mt-7 text-base sm:text-lg text-white/65 leading-relaxed max-w-md">
              The trusted peer-to-peer car rental marketplace for Dubai. Built
              with Frozen Hold deposits, 360° Check-In, and Live Fine Tracking
              — so every drive is{" "}
              <span className="font-serif-italic text-white/85">accountable</span>.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-6">
              <a
                data-testid="footer-email"
                href="mailto:support@carvny.com"
                className="group inline-flex items-center gap-2 text-sm font-bold text-white hover:text-carvny-red transition-colors"
              >
                <Mail size={16} className="text-carvny-red" />
                support@carvny.com
                <ArrowUpRight
                  size={14}
                  className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                />
              </a>
              <span className="inline-flex items-center gap-2 text-sm font-bold text-white">
                <MapPin size={16} className="text-carvny-red" />
                Dubai, UAE — Launching 2026
              </span>
            </div>
          </div>

          {/* Link columns */}
          <div className="col-span-12 lg:col-span-5 grid grid-cols-2 sm:grid-cols-3 gap-8 sm:gap-10">
            <FooterCol
              title="Product"
              links={[
                { label: "Frozen Hold", href: "#solution" },
                { label: "360° Check-In", href: "#solution" },
                { label: "Live Fine Tracking", href: "#solution" },
                { label: "How it works", href: "#how" },
              ]}
              testidPrefix="footer-product"
            />
            <FooterCol
              title="Company"
              links={[
                { label: "About", href: "#" },
                { label: "Careers", href: "#" },
                { label: "Press", href: "#" },
                { label: "Contact", href: "mailto:support@carvny.com" },
              ]}
              testidPrefix="footer-company"
            />
            <FooterCol
              title="Legal"
              links={[
                { label: "Terms", href: "#" },
                { label: "Privacy", href: "#" },
                { label: "Cookies", href: "#" },
                { label: "RTA Compliance", href: "#" },
              ]}
              testidPrefix="footer-legal"
            />
          </div>
        </div>

        {/* Divider + bottom row */}
        <div className="mt-14 sm:mt-16 pt-6 border-t border-white/10 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="text-xs sm:text-sm text-white/55">
            © 2026 CARVNY. All rights reserved.
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-white/45">
              Crafted in
            </span>
            <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-carvny-red">
              Dubai, UAE
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links, testidPrefix }) {
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-[0.28em] text-carvny-red">
        {title}
      </div>
      <ul className="mt-5 space-y-3">
        {links.map((l) => (
          <li key={l.label}>
            <a
              data-testid={`${testidPrefix}-${l.label
                .toLowerCase()
                .replace(/\s+/g, "-")}`}
              href={l.href}
              className="text-sm font-semibold text-white/80 hover:text-white transition-colors"
            >
              {l.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
